import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Column } from "primereact/column";

import ActionPlanApi from "../../services/ActionPlanApi";
import { useToast } from "../../context/ToastContext";

// Design System
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tag } from "../../components/Tag/Tag";
import PageHeader from "../../components/PageHeader/PageHeader";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "TableFilterAdvanced_ActionPlan_filters";

interface ActionPlan {
  id: string;
  name: string;
  description: string;
  type: string;
  status: number;
  operationName: string;
  forecastDate: string | null;
  completionDate: string | null;
  responsibleSector: string;
  responsiblePerson: string;
  errorProof: boolean;
  careLink: boolean;
}

const ListActionPlans: React.FC = () => {
  const [actionPlans, setActionPlans] = useState<ActionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { failureId, fmeaId } = useParams();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fmeaSource = searchParams.get("type") || "product";

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("forecastDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter states
  const [filterName, setFilterName] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [filterOp, setFilterOp] = useState("");
  const [filterForecastDate, setFilterForecastDate] = useState<Date | null>(null);
  const [filterSector, setFilterSector] = useState("");
  const [filterPerson, setFilterPerson] = useState("");

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Fetch data on mount
  useEffect(() => {
    if (failureId) {
      // Sincronizar estados individuais com filtros salvos
      if (savedFilters.name) setFilterName(savedFilters.name.value || "");
      if (savedFilters.description) setFilterDescription(savedFilters.description.value || "");
      if (savedFilters.type) setFilterType(savedFilters.type.value || "");
      if (savedFilters.status) setFilterStatus(savedFilters.status.value);
      if (savedFilters.operationName) setFilterOp(savedFilters.operationName.value || "");
      if (savedFilters.forecastDate) setFilterForecastDate(new Date(savedFilters.forecastDate.value));
      if (savedFilters.responsibleSector) setFilterSector(savedFilters.responsibleSector.value || "");
      if (savedFilters.responsiblePerson) setFilterPerson(savedFilters.responsiblePerson.value || "");

      fetchActionPlans(savedFilters);
    }
  }, [failureId]);

  const statusBodyTemplate = (rowData: ActionPlan): JSX.Element => {
    const status = rowData.status;
    let label = translateForm("planned");
    let severity: "primary" | "successInverse" | "warningInverse" | "errorInverse" = "primary";

    switch(status) {
      case 0: label = translateForm("planned"); severity = "primary"; break;
      case 1: label = translateForm("inProgress"); severity = "warningInverse"; break;
      case 2: label = translateForm("completed"); severity = "successInverse"; break;
      case 3: label = translateForm("cancelled"); severity = "errorInverse"; break;
    }

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Tag label={label} severity={severity} />
      </div>
    );
  };

  const fetchActionPlans = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    if (!failureId) {
      return;
    }

    setIsLoading(true);
    try {
      const pageNumber = Math.floor(firstIndex / pageSize) + 1;
      const forecastDateIso = filters.forecastDate?.value
        ? new Date(filters.forecastDate.value).toISOString()
        : undefined;

      const response = await ActionPlanApi.GetByFilterAsync(
        pageNumber,
        pageSize,
        field,
        direction,
        filters.name?.value || null,
        filters.description?.value || null,
        filters.type?.value || null,
        filters.status?.value != null ? filters.status.value : null,
        filters.operationName?.value || null,
        forecastDateIso,
        filters.responsibleSector?.value || null,
        filters.responsiblePerson?.value || null,
        failureId,
      );

      const responseData = response?.data;

      setActionPlans(responseData?.actionPlans || []);
      setTotalRecords(responseData?.total || 0);
    } catch (error) {
      displayToast(
        "error",
        translateForm("errorFetchingActionPlans"),
        "",
        false,
        4000,
      );
      setActionPlans([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
      type: filterType ? { value: filterType } : undefined,
      status: filterStatus !== null ? { value: filterStatus } : undefined,
      operationName: filterOp ? { value: filterOp } : undefined,
      forecastDate: filterForecastDate ? { value: filterForecastDate } : undefined,
      responsibleSector: filterSector ? { value: filterSector } : undefined,
      responsiblePerson: filterPerson ? { value: filterPerson } : undefined,
    };

    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchActionPlans(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterDescription("");
    setFilterType("");
    setFilterStatus(null);
    setFilterOp("");
    setFilterForecastDate(null);
    setFilterSector("");
    setFilterPerson("");

    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchActionPlans({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchActionPlans(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const handleSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField || "");
    setSortOrder(dir);
    fetchActionPlans(savedFilters, page, e.sortField || "", dir);
  };

  // async function deleteActionPlan(id: string): Promise<void> {
  //   try {
  //     await ActionPlanApi.DeleteActionPlan(id);
  //     setActionPlans((prev) => prev.filter((plan) => plan.id !== id));
  //     displayToast(
  //       "success",
  //       translateForm("actionPlanSuccessfullyDeleted"),
  //       "",
  //       false,
  //       3000,
  //     );
  //   } catch (error) {
  //     console.error("Erro ao excluir o plano de ação:", error);
  //     displayToast(
  //       "error",
  //       translateForm("actionPlanNotDeleted"),
  //       "",
  //       false,
  //       3000,
  //     );
  //   }
  // }

  const descriptionBodyTemplate: any = (rowData: ActionPlan) => {
    const truncatedText =
      rowData.description.length > 30
        ? `${rowData.description.substring(0, 30)}...`
        : rowData.description;
    return <span title={rowData.description}>{truncatedText}</span>;
  };

  const dateBodyTemplate = (
    rowData: ActionPlan,
    field: keyof ActionPlan,
  ): React.ReactNode => {
    const iso = rowData[field];
    if (!iso) return null;

    const formatted = String(iso)
      .substring(0, 10)
      .split("-")
      .reverse()
      .join("/");

    return <span title={formatted}>{formatted}</span>;
  };

  const actionBodyTemplate = (rowData: ActionPlan): JSX.Element => {
    return (
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
        <IconButton
          icon="assignment"
          variant="text"
          severity="secondary"
          onClick={() =>
            navigate(
              `/fmeas/${fmeaId}/failures/${failureId}/action-plans/${rowData.id}/edit`,
            )
          }
          aria-label={translateGeneral("manage")}
        />
      </div>
    );
  };



  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("actionPlans")}
        description="Gerencie os planos de ação para mitigar os riscos desta falha"
      >
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Button
            label={translateForm("back")}
            icon="arrow_back"
            variant="text"
            severity="secondary"
            onClick={() => navigate(`/fmeas/${fmeaId}/failures?type=${fmeaSource}`)}
          />
          <Button
            label={translateForm("register")}
            icon="add"
            severity="primary"
            onClick={() => navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans/create`)}
          />
        </div>
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <ConfirmDialog />
        
        <FilterPanel
          title="Filtros"
          showFilterIcon
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label="OP"
            value={filterOp}
            onChange={(val) => setFilterOp(val)}
          />
          <Input
            type="text"
            label={translateForm("title")}
            value={filterName}
            onChange={(val) => setFilterName(val)}
          />
          <Input
            type="text"
            label={translateForm("description")}
            value={filterDescription}
            onChange={(val) => setFilterDescription(val)}
          />
          <Input
            type="text"
            label={translateForm("type")}
            value={filterType}
            onChange={(val) => setFilterType(val)}
          />
          <Input
            type="select"
            label={translateForm("status")}
            value={filterStatus}
            options={[
              { label: translateForm("planned"), value: 0 },
              { label: translateForm("inProgress"), value: 1 },
              { label: translateForm("completed"), value: 2 },
              { label: translateForm("cancelled"), value: 3 },
            ]}
            onChange={(val) => setFilterStatus(val)}
          />
          <Input
            type="calendar"
            label={translateForm("forecastDate")}
            value={filterForecastDate}
            onChange={(val) => setFilterForecastDate(val)}
          />
          <Input
            type="text"
            label={translateForm("responsibleSector")}
            value={filterSector}
            onChange={(val) => setFilterSector(val)}
          />
          <Input
            type="text"
            label={translateForm("responsiblePerson")}
            value={filterPerson}
            onChange={(val) => setFilterPerson(val)}
          />
        </FilterPanel>

        <DataTable
          value={actionPlans}
          paginated
          first={page}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={handlePage}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          loading={isLoading}
          emptyMessage={translateForm("noRecordsFound") || "Nenhum registro encontrado"}
          rowsPerPageOptions={[10, 20, 50]}
          resizableColumns
        >
          <Column field="operationName" header="OP" sortable style={{ minWidth: "8rem" }} />
          <Column field="name" header={translateForm("title")} sortable style={{ minWidth: "12rem" }} />
          <Column field="description" header={translateForm("description")} body={descriptionBodyTemplate} sortable style={{ minWidth: "16rem" }} />
          <Column field="type" header={translateForm("type")} sortable style={{ minWidth: "10rem" }} />
          <Column field="status" header={translateForm("status")} body={statusBodyTemplate} sortable style={{ minWidth: "10rem" }} />
          <Column field="forecastDate" header={translateForm("forecastDate")} body={(row: ActionPlan) => dateBodyTemplate(row, "forecastDate")} sortable style={{ minWidth: "10rem" }} />
          <Column field="responsibleSector" header={translateForm("responsibleSector")} sortable style={{ minWidth: "12rem" }} />
          <Column field="responsiblePerson" header={translateForm("responsiblePerson")} sortable style={{ minWidth: "12rem" }} />
          <Column header={translateForm("actions")} body={actionBodyTemplate} style={{ width: "8rem" }} />
        </DataTable>


      </div>
    </div>
  );
};

export default ListActionPlans;
